<script>
  import { modal } from '../lib/modal.js';
  import { createEventDispatcher } from 'svelte';
  import { link } from 'svelte-spa-router';
  import { Send, ShieldCheck, AlertTriangle } from '../lib/icons/index.js';
  import { t } from '../i18n/index.js';
  import { companyInvoicesApi } from '../stores/api.js';
  import { invoiceWarningNotices } from '../lib/warnings.js';
  import ErrorBanner from './ErrorBanner.svelte';
  import InvoiceNumberField from './InvoiceNumberField.svelte';
  import { numberingPayload, numberingReady } from '../lib/numbering.js';
  import { formatDateTime, formatEuro, localizedError } from '../lib/labels.js';

  /**
   * The point of no return for a draft: validates first, shows what would be
   * refused, then shows the number the draft will receive — assigned
   * automatically, or typed by hand for recovery — and offers the irreversible
   * action.
   *
   * A draft can validate `valid: true` and still carry warnings — a stated
   * total that disagrees with the sum of the lines is exactly that case — so
   * both `issues` (blocking) and `warnings` (not blocking, but worth reading
   * before the document leaves) are shown, never only the former.
   */
  export let companyId;
  export let draftId;

  const dispatch = createEventDispatcher();

  let checking = true;
  let valid = false;
  let issues = [];
  let checkWarnings = [];
  let invoiceNumber = '';
  let manual = false;
  let submitting = false;
  let error = null;
  // Set once promotion actually succeeds: { id, invoiceNumber, status, fileId,
  // awaitingManualSignature, warnings, message }. While this holds a value the
  // dialog shows the result instead of the form — there is nothing left to
  // confirm, the document has already been issued.
  let result = null;
  // A `possible_duplicate` refusal: { matches, windowHours, token, formKey }.
  // A draft saved by a creation form is checked for a lookalike issued in the
  // last hours; opened from Bozze it must still be possible to confirm it, or
  // it would stay blocked until the window passes.
  let possibleDuplicate = null;

  // The token is valid only for the promotion it was given for: a different
  // number drops the warning, and the next «Emetti» is checked afresh.
  $: numberingKey = JSON.stringify(numberingPayload({ manual, invoiceNumber }));
  $: if (possibleDuplicate && possibleDuplicate.formKey !== numberingKey) possibleDuplicate = null;

  validate();

  async function validate() {
    checking = true;
    error = null;
    try {
      const res = await companyInvoicesApi.validateDraft(companyId, draftId);
      valid = res.valid;
      issues = res.issues || [];
      checkWarnings = invoiceWarningNotices(res.warnings);
    } catch (err) {
      error = err;
    } finally {
      checking = false;
    }
  }

  async function confirm(acknowledgement = null) {
    submitting = true;
    error = null;
    const formKey = numberingKey;
    const body = {
      ...numberingPayload({ manual, invoiceNumber }),
      ...(acknowledgement && { acknowledgeDuplicates: acknowledgement }),
    };
    try {
      result = await companyInvoicesApi.promoteDraft(companyId, draftId, body);
      possibleDuplicate = null;
    } catch (err) {
      // Refused before anything was written: nothing is issued and the draft
      // stays. A stale token answers the same way with a fresh one.
      if (err?.status === 409 && err.code === 'possible_duplicate') {
        possibleDuplicate = {
          matches: err.matches || [],
          windowHours: err.windowHours ?? null,
          token: err.duplicateCheckToken ?? null,
          formKey,
        };
      } else if (err?.status === 409 && err.code === 'payment_due_date_before_invoice_date') {
        // A due date fixed on the draft falls before the day it is being issued
        // with. The server's sentence names the instalment and both days and
        // says how to fix it — a generic «conflitto» would hide all of that.
        error = localizedError(err.message);
      } else {
        error = err;
      }
    } finally {
      submitting = false;
    }
  }

  // The API's own warnings, read the same way InvoiceCreate.svelte reads them
  // off a freshly created invoice — the wording lives in one catalog namespace
  // so a warning reads the same regardless of which screen raised it.
  function issueAnyway() {
    confirm(possibleDuplicate?.token || null);
  }

  $: resultWarnings = result ? invoiceWarningNotices(result.warnings) : [];
</script>

<!-- svelte-ignore a11y_click_events_have_key_events a11y_no_static_element_interactions -->
<div class="modal-backdrop" on:click|self={() => (result ? null : dispatch('cancel'))}>
  <div class="modal-content w-full max-w-lg p-6" role="dialog" aria-modal="true" aria-labelledby="promote-dialog-title" use:modal={{ onClose: () => (result || submitting ? null : dispatch('cancel')) }}>
    {#if result}
      <!-- Issued. The wording is the server's own — Task 7 wrote it to explain
           precisely why this cannot be undone and what the remedy is, and
           repeating it here in different words risks contradicting it. -->
      <h2 id="promote-dialog-title" class="text-lg text-heading font-display mb-1 flex items-center gap-2">
        <ShieldCheck size={20} class="text-success" />
        {result.awaitingManualSignature
          ? $t('drafts.promoteAwaitingSignatureTitle')
          : $t('drafts.promoteDoneTitle')}
      </h2>
      <p class="text-sm mt-2" style="color: var(--color-text-body);">{result.message}</p>
      <p class="mt-2 font-mono font-bold" style="color: var(--color-text-body);">{$t('numbering.assigned', { numero: result.invoiceNumber })}</p>

      {#if resultWarnings.length > 0}
        <div class="card p-4 mt-4 border-yellow-500">
          <p class="font-bold text-warning flex items-center gap-2">
            <AlertTriangle size={16} /> {$t('invoiceWarnings.title')}
          </p>
          {#each resultWarnings as notice}
            <p class="mt-2 text-xs measure" style="color: var(--color-text-body);">{notice.text}</p>
            {#if notice.detail}
              <p class="mt-1 text-xs text-muted measure">{notice.detail}</p>
            {/if}
          {/each}
        </div>
      {/if}

      <div class="flex justify-end pt-5">
        <button type="button" class="btn btn-primary btn-sm" on:click={() => dispatch('promoted', result)}>
          {$t('drafts.promoteOpenInvoice')}
        </button>
      </div>
    {:else}
      <h2 id="promote-dialog-title" class="text-lg text-heading font-display mb-1 flex items-center gap-2">
        <Send size={20} /> {$t('drafts.promoteTitle')}
      </h2>
      <p class="text-sm mb-4" style="color: var(--color-text-body);">{$t('drafts.promoteBody')}</p>

      <ErrorBanner {error} />

      {#if checking}
        <p class="text-sm text-muted">{$t('drafts.loading')}</p>
      {:else}
        <!-- Blocking: the number field stays disabled below until this is empty. -->
        {#if !valid}
          <div class="card p-4 mb-4 border-red-500">
            <p class="font-bold text-danger">{$t('drafts.promoteBlocked')}</p>
            <ul class="mt-2 space-y-1 text-xs list-disc list-inside" style="color: var(--color-text-body);">
              {#each issues as issue}<li>{issue.message || issue}</li>{/each}
            </ul>
          </div>
        {/if}

        <!-- Not blocking, but the whole reason `warnings` exists is that
             quadratura mismatch does not refuse the document — an operator
             who never saw this would only find out from the customer. -->
        {#if checkWarnings.length > 0}
          <div class="card p-4 mb-4 border-yellow-500">
            <p class="font-bold text-warning flex items-center gap-2">
              <AlertTriangle size={16} /> {$t('drafts.promoteWarningsTitle')}
            </p>
            {#each checkWarnings as notice}
              <p class="mt-2 text-xs measure" style="color: var(--color-text-body);">{notice.text}</p>
              {#if notice.detail}
                <p class="mt-1 text-xs text-muted measure">{notice.detail}</p>
              {/if}
            {/each}
          </div>
        {/if}

        <div>
          <InvoiceNumberField {companyId} {draftId} disabled={!valid} bind:manual bind:invoiceNumber />
          <p class="mt-1 text-xs text-muted">{$t('drafts.promoteDateHint')}</p>
        </div>

        <!-- The server held the promotion back: a document just like this one
             was issued in the last hours. Nothing is issued until the operator says. -->
        {#if possibleDuplicate}
          <div class="card p-4 mt-4 border-yellow-500" role="alert">
            <p class="text-sm measure flex items-start gap-2" style="color: var(--color-text-body);">
              <AlertTriangle size={16} class="text-warning shrink-0 mt-0.5" />
              <span>{$t('form.issue.possibleDuplicate', { ore: possibleDuplicate.windowHours })}</span>
            </p>
            <ul class="mt-2 space-y-1 text-sm" style="color: var(--color-text-body);">
              {#each possibleDuplicate.matches as match (match.id)}
                <li>
                  {$t('form.issue.possibleDuplicateMatch', { numero: match.invoiceNumber, emessoIl: formatDateTime(match.issuedAt), totale: formatEuro(match.totalAmount) })}
                  <a href={`/invoices/${match.id}`} use:link class="ml-2 eyebrow text-action">{$t('invoiceWarnings.openInvoice')}</a>
                </li>
              {/each}
            </ul>
            <p class="text-xs measure mt-2" style="color: var(--color-text-body);">{$t('form.issue.possibleDuplicateQuestion')}</p>
          </div>
        {/if}
      {/if}

      {#if possibleDuplicate}
        <div class="flex justify-end gap-3 pt-5">
          <button type="button" class="btn btn-secondary btn-sm" on:click={() => dispatch('cancel')} disabled={submitting}>
            {$t('form.issue.cancelIssue')}
          </button>
          <button type="button" class="btn btn-primary btn-sm" disabled={submitting} on:click={issueAnyway}>
            {submitting ? $t('drafts.promoting') : $t('form.issue.issueAnyway')}
          </button>
        </div>
      {:else}
      <div class="flex justify-end gap-3 pt-5">
        <button type="button" class="btn btn-secondary btn-sm" on:click={() => dispatch('cancel')} disabled={submitting}>
          {$t('drafts.promoteCancel')}
        </button>
        <button
          type="button"
          class="btn btn-primary btn-sm"
          disabled={checking || !valid || !numberingReady({ manual, invoiceNumber }) || submitting}
          on:click={() => confirm()}
        >
          {submitting ? $t('drafts.promoting') : $t('drafts.promoteConfirm')}
        </button>
      </div>
      {/if}
    {/if}
  </div>
</div>
